import React, { useState, useEffect } from 'react'
import { createFormBag, FormBag, FormData, FormErrors, FormUpdateEvent, validateFormBag } from 'react-formage'
import { Button, ButtonGroup } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Modal } from 'components/ui/modal'
import { StatusGuard } from 'components/ui/status-guard'

import { sharedActionCreators } from 'containers/shared'
import { connect } from 'containers/store'

import { pick } from 'helpers/core'
import { Status } from 'helpers/status'
import { actionCreators } from '../../../actions'
import { GettyStore } from 'domains/org/getty/store';
import { categorySchema } from '../../../schemas/category'
import { Site } from 'generated/mos/structure'
import { CreateTourCategoryRequest } from 'generated/mos/org/getty/audiomanagement'

type ConnectedProps = {
  categoryCreate: GettyStore['getty']['categoryCreate'];
  categories: GettyStore['getty']['categories'];
};

type DirectProps = {
  readonly onClose: () => void;
  readonly siteRef: Site.Ref;
}

type ActionProps =
  Pick<typeof actionCreators, 'gettyCreateTourCategoryRequest'> &
  Pick<typeof sharedActionCreators, 'toastNotification'>;

type Props = ConnectedProps & DirectProps & ActionProps;

type FormValues = {
  readonly categoryName: string;
};


const CategoryCreateView = (props: Props) => {
  const {
    onClose,
    toastNotification,
    categoryCreate,
    categories
  } = props

  const bag: FormBag<FormValues> = createFormBag({
    categoryName: '',
  })
  const [ formBag, setFormBag ] = useState(bag)

  useEffect(() => {
    if (categoryCreate.status === Status.Failed) {
      toastNotification({ type: 'error', text: 'There was a problem creating the category.' })
    }

    if (
      categoryCreate.status === Status.Complete
    ) {
      toastNotification({ type: 'success', text: 'Your category has been created.' })
      onClose()
    }
  }, [categoryCreate, onClose, toastNotification])


  const onFormUpdate = (event: FormUpdateEvent<FormValues>) => {
    setFormBag(event.bag)
  }

  const onFormValidate = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {}

    if (!values.categoryName || !values.categoryName.trim().length) {
      errors.categoryName = 'Category name required';
    }

    return errors
  }

  const save = () => {
    const bag = validateFormBag(formBag, onFormValidate)
    setFormBag(bag)

    if (!bag.valid) {
      return
    }

    const { gettyCreateTourCategoryRequest, siteRef } = props
    const { categoryName: name } = bag.values
    if (name) {
      gettyCreateTourCategoryRequest({
        ...CreateTourCategoryRequest.defaults,
        name: categorySchema.name ? categorySchema.name.write(name) : name,
        siteRef,
        siteOrder: categories.status === Status.Ready && categories.data.tourCategories.length > 0 ? categories.data.tourCategories[categories.data.tourCategories.length - 1].siteOrder + 1 : 0
      })
    }
  }

  const { errors, touched, valid } = formBag
  const loading = categoryCreate.status !== Status.Complete && categoryCreate.status !== Status.Idle
  
  const footer = (
    <ButtonGroup>
      <Button
        type="button"
        key="cancel"
        appearance="secondary"
        onClick={(event) => { event.preventDefault(); onClose(); }}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        isDisabled={loading || !valid}
        onClick={() => save()}
      >
        {loading ? 'Creating...' : 'Create'}
      </Button>
    </ButtonGroup>
  )

  return (
    <Modal onClose={onClose} header={<h3>Create category</h3>} footer={footer}>
      <form noValidate onSubmit={(event) => { event.preventDefault(); save(); }}>
        <StatusGuard status={{ status: Status.Idle }} mode="form">
          <FormData
            bag={formBag}
            onUpdate={onFormUpdate}
            validate={onFormValidate}
          >
            <Input
              label="Category name"
              field="categoryName"
              error={touched.categoryName ? errors.categoryName : undefined}
            />
          </FormData>
        </StatusGuard>
      </form>
    </Modal>
  )
}

export const CategoryCreate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore>(
  ({ getty }) => pick(getty, 'categoryCreate', 'categories'),
  {
    ...pick(actionCreators, 'gettyCreateTourCategoryRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(CategoryCreateView)
